import React, { useState } from "react";
import { Text } from "src/sudokit/components/Text";
import { Textarea } from "src/sudokit/components/Textarea";
import { SudoButton } from "src/sudokit/components/SudoButton";
import MenuSelect from "src/sudokit/components/SudoSelect/MenuSelect";
import { DEFAULT_IMAGE_GENERATION_MODEL_ID } from "~shared/imageGenerationModels";
import Field from "./shared/Field";
import ModelSelect from "./shared/ModelSelect";
import PanelLayout from "./shared/PanelLayout";
import ResultPanel from "./shared/ResultPanel";
import StylePresetEditor from "./shared/StylePresetEditor";
import { useImageGeneration } from "./shared/useImageGeneration";
import { useStylePresets } from "./shared/useStylePresets";
import { CUSTOM_STYLE_PRESET_ID } from "./shared/types";

const PRESETS_ENDPOINT = "/api/admin/imageGenTest/cover/presets";
const GENERATE_ENDPOINT = "/api/admin/imageGenTest/cover/generate";

const TITLE_MODE_OPTIONS = [
  { value: "overlay", label: "Overlay — art only, title set in HTML later" },
  { value: "baked", label: "Baked — letter the title into the art (experimental)" },
];

const CoverPanel: React.FC = () => {
  const [title, setTitle] = useState("");
  const [genre, setGenre] = useState("");
  const [synopsis, setSynopsis] = useState("");
  const [style, setStyle] = useState("");
  const [braindump, setBraindump] = useState("");
  const [conceptOverride, setConceptOverride] = useState("");
  const [titleMode, setTitleMode] = useState("overlay");
  const [model, setModel] = useState<string>(DEFAULT_IMAGE_GENERATION_MODEL_ID);

  const presets = useStylePresets(PRESETS_ENDPOINT);
  const { loading, imageUrl, result, error, generate } = useImageGeneration();

  const hasSummarizerInput = Boolean(conceptOverride.trim() || title.trim() || genre.trim() || synopsis.trim() || braindump.trim());
  const canGenerate =
    Boolean(presets.stylePresetId) &&
    hasSummarizerInput &&
    (presets.stylePresetId !== CUSTOM_STYLE_PRESET_ID || Boolean(presets.stylePresetText.trim()));

  const handleGenerate = () =>
    generate(GENERATE_ENDPOINT, {
      title: title || undefined,
      genre: genre || undefined,
      synopsis: synopsis || undefined,
      style: style || undefined,
      braindump: braindump || undefined,
      concept: conceptOverride.trim() || undefined,
      stylePresetId: presets.stylePresetId,
      stylePresetText: presets.stylePresetText || undefined,
      titleMode,
      model,
    });

  const conceptPreview = result
    ? [result.concept, result.motifs ? `Motifs: ${result.motifs}` : null, result.mood ? `Mood: ${result.mood}` : null].filter(Boolean).join("\n\n")
    : null;

  return (
    <PanelLayout
      result={<ResultPanel loading={loading} imageUrl={imageUrl} result={result} downloadName="project-cover-test.png" aspectRatio="2:3" conceptPreview={conceptPreview} />}
    >
      <div className="flex flex-col gap-3 w-full text-left">
        <Text size="sm" weight="bold">
          Project / Story Bible
        </Text>
        <Field id="coverTitle" label="Title" value={title} onChange={setTitle} optional placeholder="e.g. The Salt Road" />
        <Field id="coverGenre" label="Genre" value={genre} onChange={setGenre} optional placeholder="e.g. literary thriller" />
        <Field id="coverSynopsis" label="Synopsis" value={synopsis} onChange={setSynopsis} optional multiline minRows={5} placeholder="A paragraph or two of the story's premise" />
        <Field id="coverStyle" label="Prose style" value={style} onChange={setStyle} optional placeholder="e.g. spare, atmospheric" />
        <Field id="coverBraindump" label="Premise notes" value={braindump} onChange={setBraindump} optional multiline minRows={3} placeholder="Optional extra premise / braindump text" />
      </div>

      <div className="flex flex-col gap-2 w-full text-left border-t border-app-menu-color-hover pt-6">
        <label htmlFor="coverConceptOverride" className="text-sm font-medium">
          Concept override <span className="text-app-grey-1 font-normal">(optional)</span>
        </label>
        <Textarea mode="default" id="coverConceptOverride" value={conceptOverride} onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setConceptOverride(e.target.value)} placeholder="Describe the cover image directly to skip the concept summarizer" minRows={3} />
        <Text size="xs" color="grey-1">
          Leave blank to have the model derive a cover concept from the fields above.
        </Text>
      </div>

      <div className="flex flex-col gap-2 w-full text-left">
        <Text size="sm" weight="medium">
          Title Treatment
        </Text>
        <MenuSelect options={TITLE_MODE_OPTIONS} value={titleMode} onChange={setTitleMode} label="Title treatment" variant="secondary" menuAlign="start" />
      </div>

      <StylePresetEditor
        presets={presets.presets}
        presetsLoading={presets.presetsLoading}
        presetsError={presets.presetsError}
        stylePresetId={presets.stylePresetId}
        onStylePresetIdChange={presets.setStylePresetId}
        text={presets.stylePresetText}
        onTextChange={presets.setStylePresetText}
        onResetText={presets.resetStylePresetText}
        isTextDefault={presets.isStylePresetTextDefault}
        promptHelpText="Edit this to iterate on the cover style wording — sent as-is with the generated concept."
      />

      <ModelSelect value={model} onChange={setModel} aspectRatioAware helpText="Covers are always 2:3. Recraft and FLUX are hidden here — they ignore the aspect ratio." />

      <SudoButton type="button" variant="primary" state={loading ? "loading" : "default"} disabled={loading || !canGenerate} onClick={handleGenerate}>
        {loading ? "Generating..." : "Generate"}
      </SudoButton>

      {error && (
        <Text size="sm" color="danger-color">
          {error}
        </Text>
      )}
    </PanelLayout>
  );
};

export default CoverPanel;
